/** The live support desk's wire shapes (117U S3), shared by the Worker and the pages that listen. */
import type { SupportMessage } from "./types";
import type { SupportStatus } from "./schemas";

export type DeskEvent =
  | { t: "message"; threadId: string; message: SupportMessage; staffName: string | null }
  | { t: "typing"; threadId: string; name: string }
  | { t: "status"; threadId: string; status: SupportStatus };

/** One ongoing conversation in the operator's floating inbox. */
export interface DeskInboxItem {
  id: string;
  kind: "seller" | "buyer";
  subject: string;
  status: SupportStatus;
  who: string | null;
  email: string | null;
  preview: string;
  previewSender: SupportMessage["sender"] | null;
  unread: boolean;
  lastMessageAt: string;
  assignedTo: string | null;
  aiPaused: boolean;
  /** A buyer's chat Bantuan is answering by itself: no one on the team has a part in it yet (118I S3). */
  aiOnly: boolean;
  /** The Bantuan answer behind the last line, for "Kenapa AI jawab begini". */
  lastTurnId?: string | null;
  /** The regular seller's store the chat was handed to (118I S5). */
  ownerStore?: string | null;
  /** The store whose page the chat started on, so a visitor with no name still says where they are (120B). */
  fromStore?: string | null;
  /** Packages this email has paid for, newest first (120B S3); empty when none or no email. */
  packages?: string[];
}

/** One order on Kad Pembeli: what the person bought (or tried to), in any store. */
export interface DeskPackage {
  orderId: string;
  product: string;
  store: string;
  amount: number;
  currency: string;
  status: "paid" | "pending" | "failed" | "refunded";
  /** Paid at, or ordered at when never paid. */
  at: string;
  access: "active" | "revoked" | null;
}

/**
 * Kad Pembeli (120B): who is behind a chat, for the staff answering it — the AI keeps its masked
 * Kad Pelanggan. `email` is known only once proven (code or signed-in session) or from the account.
 */
export interface DeskBuyer {
  kind: "seller" | "buyer";
  name: string | null;
  email: string | null;
  /** The email (and phone) are what a visitor typed for staff, not proven (120B S2). */
  emailTyped: boolean;
  phone: string | null;
  /** Where the chat started: contact, home, account, store, tq for a buyer. */
  source: string;
  /** Host + path of the page it started on, when known. */
  page: string | null;
  store: { name: string; url: string } | null;
  /** A Sellhiz account under that email: whether it sells, and on which plan. */
  account: { isSeller: boolean; plan: string | null } | null;
  /** Newest first, at most 10. */
  orders: DeskPackage[];
}

/**
 * The floating inbox's tabs (118I S3): what needs a person (the badge counts only these), the buyers'
 * chats Bantuan is answering by itself, and everything.
 */
export const DESK_INBOX_TABS = ["staff", "ai", "seller", "all"] as const;
export type DeskInboxTab = (typeof DESK_INBOX_TABS)[number];

/** How long a "sedang menaip" line stays up after the last keystroke event. */
export const DESK_TYPING_MS = 6_000;
/** The page sends at most one typing event this often while someone types. */
export const DESK_TYPING_EVERY_MS = 3_000;

// ---- Waktu & saluran (117U S5) ----------------------------------------------

/** One special opening the operator adds: a launch night, a sale weekend. Times are ISO (UTC). */
export interface DeskSpecial {
  id: string;
  label: string;
  start: string;
  end: string;
}

/** How the support desk runs, set by the operator in Sokongan → Waktu & saluran. */
export interface DeskSettings {
  /** Office hours, Malaysia time. days: 1 = Monday … 7 = Sunday. */
  hours: { days: number[]; open: string; close: string };
  specials: DeskSpecial[];
  /** Where a customer can reach a person. WhatsApp as digits with country code; Telegram as a username. */
  channels: { whatsapp: string | null; telegram: string | null; email: string | null };
  /** Shown in sellhiz.win/contact's footer. */
  company: { name: string | null; regNo: string | null; address: string | null };
  /**
   * The superadmin's stores switched OFF (118I S1). Every other store a
   * platform owner has gets the Bantuan chat, new ones included, so nobody
   * has to remember to tick them.
   */
  saExcluded: string[];
  /**
   * Regular sellers' stores (118I S5): on, a buyer's question about a seller's own product goes to
   * that seller and the chat runs on their store pages; off (the default), the seller's contact is shown.
   */
  sellerStores: boolean;
  /**
   * A seller who has not answered a handed chat in `hours` (118I S7): on, the chat goes up to SA's
   * Perlu staf and the buyer is told; off (the default), the seller gets one more email instead.
   * The buyer's "Belum selesai" does the same.
   */
  escalate: { on: boolean; hours: number };
  /**
   * Which Claude model answers support (117U S6), and Bantuan's monthly AI budget in RM (117U S8).
   * 119O S9: `answerModel` writes Bantuan's own answers (when no template fits); `model` still picks
   * the template, cheaply. "same" = the picker's model for both.
   */
  ai: { model: SupportModelChoice; budgetRm: number; answerModel: AnswerModelChoice };
  /**
   * Where Bantuan runs (117U S15), for the staged rollout: /account and the
   * ticked stores (with their thank-you pages) first, then /contact and the
   * homepage, then WhatsApp/Telegram pointed at it.
   */
  surfaces: DeskSurfaces;
  /** Share of unflagged answers (0–50%) sampled into Mutu's review queue, so staff check what nobody complained about. */
  shadowPct: number;
  /**
   * 119O S3: "tak dapat akses" and "link kelas" are answered by sending the access email straight to the
   * address the buyer types, with no code first (it only ever reaches the buyer's own inbox). Off: the code first.
   */
  noCodeAccess: boolean;
  /**
   * 119O S5: who hears that a chat needs a person. `emails` empty = every platform admin. Telegram
   * goes to `telegramChatId` once SA has saved a bot token (kept encrypted, never in these settings).
   * Unanswered after `remindMins` within office hours, they are told once more (0 = never).
   */
  alerts: { emails: string[]; telegramChatId: string | null; remindMins: number };
}

export const DESK_SURFACES = ["account", "stores", "tq", "contact", "home"] as const;
export type DeskSurface = (typeof DESK_SURFACES)[number];
export type DeskSurfaces = Record<DeskSurface, boolean>;

export const SUPPORT_MODEL_CHOICES = ["auto", "claude-haiku-5-5", "claude-haiku-4-5"] as const;
export type SupportModelChoice = (typeof SUPPORT_MODEL_CHOICES)[number];
export const ANSWER_MODEL_CHOICES = ["claude-sonnet-5-5", "same"] as const;
export type AnswerModelChoice = (typeof ANSWER_MODEL_CHOICES)[number];

export const DEFAULT_DESK_SETTINGS: DeskSettings = {
  hours: { days: [1, 2, 3, 4, 5, 6], open: "08:00", close: "17:00" },
  specials: [],
  channels: { whatsapp: "601113052843", telegram: "masterysuccessupport", email: null },
  company: { name: null, regNo: null, address: null },
  saExcluded: [],
  sellerStores: false,
  escalate: { on: false, hours: 24 },
  ai: { model: "auto", budgetRm: 30, answerModel: "claude-sonnet-5-5" },
  // Week one of the agreed rollout: /account and the ticked stores; /contact and the homepage after.
  surfaces: { account: true, stores: true, tq: true, contact: false, home: false },
  shadowPct: 10,
  noCodeAccess: true,
  alerts: { emails: [], telegramChatId: null, remindMins: 30 },
};

const DAY_BM = ["", "Isn", "Sel", "Rab", "Kha", "Jum", "Sab", "Ahd"];

/** "Isn–Sab" for a run of days, "Isn, Rab, Jum" otherwise. */
export function daysLabel(days: number[]): string {
  const d = [...days].sort((a, b) => a - b);
  if (!d.length) return "";
  const run = d.every((x, i) => i === 0 || x === d[i - 1] + 1);
  return run && d.length > 2 ? `${DAY_BM[d[0]]}–${DAY_BM[d[d.length - 1]]}` : d.map((x) => DAY_BM[x]).join(", ");
}

/**
 * The auto-reply staff paste into WhatsApp Business and the Telegram bot
 * (117U S15): it sends people to Bantuan first, and says when a person answers.
 */
export function autoReplyText(s: DeskSettings, chatUrl: string): string {
  return [
    "Hai! Terima kasih kerana menghubungi kami.",
    `Untuk jawapan serta-merta tentang pesanan, akses, kelas, sijil dan resit, tanya Bantuan di sini: ${chatUrl}`,
    `Staf kami membalas di sini dalam waktu pejabat (${daysLabel(s.hours.days)}, ${s.hours.open}–${s.hours.close}).`,
  ].join("\n\n");
}

/** A store's place in Bantuan: one of the platform owners' (sa), a regular seller's (118I S5), or not one Bantuan serves. */
export type DeskStoreKind = "sa" | "seller" | null;

/** The platform owners' stores that get Bantuan: all of them, less the ones SA switched off. */
export const deskStores = (s: Pick<DeskSettings, "saExcluded">, platformStoreIds: string[]): string[] => platformStoreIds.filter((id) => !s.saExcluded.includes(id));

/** Whether Bantuan answers on this page: one of Sellhiz's own, or a desk store's (its home, or an order's thank-you page). */
export function surfaceOn(s: DeskSettings, surface: "contact" | "home" | "account" | "store" | "tq", storeId: string | null, kind: DeskStoreKind = null): boolean {
  if (storeId) {
    const served = kind === "sa" ? !s.saExcluded.includes(storeId) : kind === "seller" && s.sellerStores;
    return served && (surface === "tq" ? s.surfaces.tq : s.surfaces.stores);
  }
  if (surface === "contact" || surface === "home" || surface === "account") return s.surfaces[surface];
  return false;
}

/**
 * The link a buyer was sent asks for Bantuan: an email's "Perlukan bantuan?" (?bantuan=1) or a staff
 * reply (?t=…). 120M: the pages ads land on (sellhiz.win's homepage, a store's root) carry no Bantuan
 * button, so a visitor about to buy is never pulled into a chat; Bantuan opens there only from such a link.
 */
export function bantuanAsked(search: string): boolean {
  const q = new URLSearchParams(search);
  return Boolean(q.get("t")) || q.get("bantuan") === "1";
}

/** What any page may know: whether a person is on now, and how to reach one. */
export interface PublicDesk {
  open: boolean;
  /** The special opening running now, if that is why it is open. */
  special: string | null;
  hours: DeskSettings["hours"];
  channels: DeskSettings["channels"];
  company: DeskSettings["company"];
}

/** WhatsApp and Telegram are offered only after this many answers the customer was not happy with, and only while staff are on. */
export const MISSES_FOR_CHANNELS = 2;

const MY_OFFSET_MS = 8 * 3_600_000;
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const minutes = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

/** Open now? Inside a special opening, or on an office day between the office hours (Malaysia time, which has no DST). */
export function deskOpen(s: DeskSettings, nowMs: number): { open: boolean; special: string | null } {
  const special = s.specials.find((x) => Date.parse(x.start) <= nowMs && nowMs < Date.parse(x.end));
  if (special) return { open: true, special: special.label };
  const my = new Date(nowMs + MY_OFFSET_MS);
  const day = my.getUTCDay() === 0 ? 7 : my.getUTCDay();
  const m = my.getUTCHours() * 60 + my.getUTCMinutes();
  return { open: s.hours.days.includes(day) && m >= minutes(s.hours.open) && m < minutes(s.hours.close), special: null };
}

const DAY_NAMES = { bm: ["", "Isnin", "Selasa", "Rabu", "Khamis", "Jumaat", "Sabtu", "Ahad"], en: ["", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] };

/** "Isnin–Sabtu" for a run of days, else the days listed. */
export function dayRangeLabel(days: number[], lang: "bm" | "en"): string {
  const n = DAY_NAMES[lang];
  const sorted = [...days].sort((a, b) => a - b);
  const run = sorted.length > 2 && sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  return run ? `${n[sorted[0]]}–${n[sorted[sorted.length - 1]]}` : sorted.map((d) => n[d]).join(", ");
}

/** 119O S5: handed to staff outside office hours: say when they will answer, not "now". */
export function staffClosedText(s: DeskSettings, lang: "bm" | "en"): string {
  const when = `${dayRangeLabel(s.hours.days, lang)} ${s.hours.open}–${s.hours.close}`;
  return lang === "en"
    ? `Our staff are in ${when}. Your question is with them now, and they will reply here and by email as soon as they are in.`
    : `Staf kami bertugas ${when}. Soalan anda sudah diserahkan kepada mereka, dan mereka akan balas di sini dan melalui emel anda sebaik masuk kerja.`;
}

const str = (v: unknown, max: number): string | null => (typeof v === "string" && v.trim() ? v.trim().slice(0, max) : null);

/** Whatever was saved (or sent), as settings the desk can trust; anything missing or malformed falls back to the default. */
export function normalizeDeskSettings(raw: unknown): DeskSettings {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const h = (o.hours && typeof o.hours === "object" ? o.hours : {}) as Record<string, unknown>;
  const days = Array.isArray(h.days) ? [...new Set(h.days.filter((d): d is number => Number.isInteger(d) && d >= 1 && d <= 7))].sort() : DEFAULT_DESK_SETTINGS.hours.days;
  const open = typeof h.open === "string" && HHMM.test(h.open) ? h.open : DEFAULT_DESK_SETTINGS.hours.open;
  const close = typeof h.close === "string" && HHMM.test(h.close) && minutes(h.close) > minutes(open) ? h.close : DEFAULT_DESK_SETTINGS.hours.close;
  const specials = (Array.isArray(o.specials) ? o.specials : [])
    .map((x) => (x && typeof x === "object" ? (x as Record<string, unknown>) : {}))
    .filter((x) => typeof x.start === "string" && typeof x.end === "string" && Date.parse(x.start as string) < Date.parse(x.end as string))
    .slice(0, 20)
    .map((x, i) => ({ id: str(x.id, 40) ?? `s${i + 1}`, label: str(x.label, 60) ?? "Waktu khas", start: new Date(x.start as string).toISOString(), end: new Date(x.end as string).toISOString() }));
  const c = (o.channels && typeof o.channels === "object" ? o.channels : DEFAULT_DESK_SETTINGS.channels) as Record<string, unknown>;
  const wa = str(c.whatsapp, 20)?.replace(/\D/g, "") || null;
  const tg = str(c.telegram, 40)?.replace(/^@|^https?:\/\/t\.me\//, "") || null;
  const email = str(c.email, 120);
  const co = (o.company && typeof o.company === "object" ? o.company : {}) as Record<string, unknown>;
  // The old tick list (storeIds) is ignored on purpose: every SA store is on unless excluded.
  const saExcluded = Array.isArray(o.saExcluded) ? [...new Set(o.saExcluded.filter((x): x is string => typeof x === "string" && /^[0-9a-f-]{36}$/i.test(x)))].slice(0, 100) : [];
  return {
    hours: { days, open, close },
    specials,
    channels: { whatsapp: wa && wa.length >= 9 ? wa : null, telegram: tg && /^[A-Za-z0-9_]{4,40}$/.test(tg) ? tg : null, email: email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null },
    company: { name: str(co.name, 120), regNo: str(co.regNo, 60), address: str(co.address, 300) },
    saExcluded,
    sellerStores: o.sellerStores === true,
    escalate: ((e) => ({
      on: e.on === true,
      hours: ((h) => (Number.isFinite(h) ? Math.max(1, Math.min(168, Math.round(h))) : 24))(Number(e.hours ?? 24)),
    }))((o.escalate && typeof o.escalate === "object" ? o.escalate : {}) as Record<string, unknown>),
    ai: {
      model: (SUPPORT_MODEL_CHOICES as readonly string[]).includes(String((o.ai as Record<string, unknown> | undefined)?.model)) ? (o.ai as { model: SupportModelChoice }).model : "auto",
      budgetRm: ((b) => (Number.isFinite(b) && b >= 0 && b <= 10_000 ? Math.round(b * 100) / 100 : 30))(Number((o.ai as Record<string, unknown> | undefined)?.budgetRm ?? 30)),
      answerModel: ANSWER_MODEL_CHOICES.find((m) => m === (o.ai as Record<string, unknown> | undefined)?.answerModel) ?? "claude-sonnet-5-5",
    },
    surfaces: Object.fromEntries(
      DESK_SURFACES.map((k) => {
        const v = (o.surfaces && typeof o.surfaces === "object" ? (o.surfaces as Record<string, unknown>) : {})[k];
        return [k, typeof v === "boolean" ? v : DEFAULT_DESK_SETTINGS.surfaces[k]];
      }),
    ) as DeskSurfaces,
    shadowPct: ((p) => (Number.isFinite(p) ? Math.max(0, Math.min(50, Math.round(p))) : 10))(Number(o.shadowPct ?? 10)),
    noCodeAccess: o.noCodeAccess !== false,
    alerts: ((a) => ({
      emails: (Array.isArray(a.emails) ? a.emails : [])
        .map((e) => (typeof e === "string" ? e.trim().toLowerCase() : ""))
        .filter((e, i, all) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) && all.indexOf(e) === i)
        .slice(0, 10),
      telegramChatId: typeof a.telegramChatId === "string" && /^(-?\d{3,20}|@[A-Za-z0-9_]{4,40})$/.test(a.telegramChatId.trim()) ? a.telegramChatId.trim() : null,
      remindMins: ((m) => (Number.isFinite(m) ? Math.max(0, Math.min(240, Math.round(m))) : 30))(Number(a.remindMins ?? 30)),
    }))((o.alerts && typeof o.alerts === "object" ? o.alerts : {}) as Record<string, unknown>),
  };
}

/** One of the platform owners' stores, for ticking which get the buyers' chat. */
export interface DeskSaStore {
  id: string;
  name: string;
  slug: string;
  /** Paid orders in the last 30 days. */
  paid30: number;
  /** The store's own home with the chat open (for WhatsApp/Telegram auto-replies). */
  chatUrl?: string;
}

/** One Pembantu answer the seller was not happy with: Jurang AI. */
export interface DeskGap {
  id: string;
  storeName: string | null;
  /** 118R: who asked, and from which screen. */
  sellerName: string | null;
  sellerEmail: string | null;
  screen: string | null;
  question: string;
  reply: string;
  /** gap = the Panduan had no answer; down = the seller gave it a thumbs down. */
  reason: "gap" | "down";
  reviewedAt: string | null;
  createdAt: string;
}
